import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { WFD_OBJECTIVES, detectObjectives, calculateObjectiveMultiplier } from '../data/wfdObjectives';
import { ARFD_OBJECTIVES, detectArfdObjectives, calculateArfdScore } from '../data/arfdObjectives';

function ObjectivesTracker({ token, apiUrl, contacts, isAdmin, onUpdate, eventType = 'WFD' }) {
  const [objectives, setObjectives] = useState({});
  const [loading, setLoading] = useState(true);
  // For the ARFD youth_operators per-unit bonus
  const [youthCount, setYouthCount] = useState(0);

  const isArfd = eventType === 'ARFD';

  const fetchObjectives = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/objectives`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setObjectives(response.data);
      // Restore saved youth count if present
      if (response.data['youth_operators']?.notes) {
        const n = parseInt(response.data['youth_operators'].notes, 10);
        if (!isNaN(n)) setYouthCount(n);
      }
    } catch (err) {
      console.error('Error fetching objectives:', err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    fetchObjectives();
  }, [fetchObjectives]);

  // Auto-detect based on the active contest
  const autoDetected = useMemo(() => {
    return isArfd ? detectArfdObjectives(contacts) : detectObjectives(contacts);
  }, [contacts, isArfd]);

  const toggleObjective = async (id, currentState) => {
    if (!isAdmin) return;
    try {
      await axios.put(`${apiUrl}/api/objectives/${id}`, {
        completed: !currentState,
        autoDetected: autoDetected[id] || false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setObjectives(prev => ({
        ...prev,
        [id]: { ...prev[id], completed: !currentState }
      }));
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error updating objective:', err);
    }
  };

  // Save youth count into the objective notes field for persistence
  const handleYouthCountChange = async (count) => {
    const n = Math.max(0, Math.min(5, parseInt(count, 10) || 0));
    setYouthCount(n);
    const isCompleted = n > 0;
    try {
      await axios.put(`${apiUrl}/api/objectives/youth_operators`, {
        completed: isCompleted,
        autoDetected: false,
        notes: String(n)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setObjectives(prev => ({
        ...prev,
        youth_operators: { ...prev['youth_operators'], completed: isCompleted, notes: String(n) }
      }));
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error saving youth count:', err);
    }
  };

  // ── WFD scoring ──────────────────────────────────────────────────────────
  const wfdScore = useMemo(() => {
    if (isArfd) return null;
    const completed = {};
    WFD_OBJECTIVES.forEach(obj => {
      completed[obj.id] = objectives[obj.id]?.completed || autoDetected[obj.id];
    });
    const multiplier = calculateObjectiveMultiplier(completed);
    const qsoPoints = contacts.reduce((sum, c) => {
      const mode = c.mode?.toUpperCase();
      if (mode === 'CW' || ['RTTY', 'PSK31', 'OLIVIA', 'JS8', 'FT8', 'FT4'].includes(mode)) {
        return sum + 2;
      }
      return sum + 1;
    }, 0);
    const completedCount = WFD_OBJECTIVES.filter(obj =>
      objectives[obj.id]?.completed || autoDetected[obj.id]
    ).length;
    return { qsoPoints, multiplier: multiplier + 1, finalScore: qsoPoints * (multiplier + 1), completedCount };
  }, [isArfd, contacts, objectives, autoDetected]);

  // ── ARFD scoring ─────────────────────────────────────────────────────────
  const arfdScore = useMemo(() => {
    if (!isArfd) return null;
    const completed = {};
    ARFD_OBJECTIVES.forEach(obj => {
      completed[obj.id] = objectives[obj.id]?.completed || autoDetected[obj.id];
    });
    const score = calculateArfdScore(contacts, completed, youthCount);
    const completedCount = ARFD_OBJECTIVES.filter(obj =>
      objectives[obj.id]?.completed || autoDetected[obj.id]
    ).length;
    return { ...score, completedCount };
  }, [isArfd, contacts, objectives, autoDetected, youthCount]);

  if (loading) {
    return <div className="card"><p>Loading objectives...</p></div>;
  }

  // ── WFD render ────────────────────────────────────────────────────────────
  if (!isArfd) {
    return (
      <div className="card objectives-card">
        <h2 className="card-title">WFD Objectives</h2>

        <div className="objectives-summary">
          <div className="objective-stat">
            <span className="objective-stat-value">{wfdScore.completedCount}/{WFD_OBJECTIVES.length}</span>
            <span className="objective-stat-label">Completed</span>
          </div>
          <div className="objective-stat">
            <span className="objective-stat-value">x{wfdScore.multiplier}</span>
            <span className="objective-stat-label">Multiplier (OM+1)</span>
          </div>
          <div className="objective-stat">
            <span className="objective-stat-value">{wfdScore.qsoPoints}</span>
            <span className="objective-stat-label">QSO Points</span>
          </div>
          <div className="objective-stat highlight">
            <span className="objective-stat-value">{wfdScore.finalScore.toLocaleString()}</span>
            <span className="objective-stat-label">Total Score</span>
          </div>
        </div>

        <div className="objectives-list">
          {WFD_OBJECTIVES.map(obj => {
            const isCompleted = objectives[obj.id]?.completed || autoDetected[obj.id];
            const isAuto = autoDetected[obj.id];
            return (
              <div
                key={obj.id}
                className={`objective-item ${isCompleted ? 'completed' : ''} ${isAuto ? 'auto-detected' : ''}`}
                onClick={() => !isAuto && toggleObjective(obj.id, objectives[obj.id]?.completed)}
                style={{ cursor: isAdmin && !isAuto ? 'pointer' : 'default' }}
              >
                <div className="objective-checkbox">{isCompleted ? '✓' : ''}</div>
                <div className="objective-content">
                  <div className="objective-header">
                    <span className="objective-name">{obj.name}</span>
                    <span className="objective-multiplier">OM x{obj.multiplier}</span>
                  </div>
                  <p className="objective-description">{obj.description}</p>
                  {isAuto && <span className="auto-badge">Auto-detected</span>}
                </div>
              </div>
            );
          })}
        </div>

        {!isAdmin && (
          <p className="objectives-note">Only admins can manually mark objectives as complete.</p>
        )}
      </div>
    );
  }

  // ── ARFD render ───────────────────────────────────────────────────────────
  return (
    <div className="card objectives-card">
      <h2 className="card-title">ARRL Field Day Bonuses</h2>

      <div className="objectives-summary">
        <div className="objective-stat">
          <span className="objective-stat-value">{arfdScore.completedCount}/{ARFD_OBJECTIVES.length}</span>
          <span className="objective-stat-label">Bonuses</span>
        </div>
        <div className="objective-stat">
          <span className="objective-stat-value">{arfdScore.rawQsoPoints}</span>
          <span className="objective-stat-label">QSO Points</span>
        </div>
        <div className="objective-stat">
          <span className="objective-stat-value">
            x{arfdScore.powerMultiplier}
          </span>
          <span className="objective-stat-label">Power Mult.</span>
        </div>
        <div className="objective-stat">
          <span className="objective-stat-value">+{arfdScore.bonusPoints}</span>
          <span className="objective-stat-label">Bonus Points</span>
        </div>
        <div className="objective-stat highlight">
          <span className="objective-stat-value">{arfdScore.finalScore.toLocaleString()}</span>
          <span className="objective-stat-label">Total Score</span>
        </div>
      </div>

      <div className="objectives-list">
        {ARFD_OBJECTIVES.map(obj => {
          const isCompleted = objectives[obj.id]?.completed || autoDetected[obj.id];
          const isAuto = autoDetected[obj.id];

          // Value display label varies by type
          let valueLabel = null;
          if (obj.type === 'multiplier') {
            valueLabel = `x${obj.value} QSO pts`;
          } else if (obj.type === 'per_unit') {
            valueLabel = `${obj.value} pts/unit (max ${obj.maxValue})`;
          } else {
            valueLabel = `+${obj.value} pts`;
          }

          return (
            <div
              key={obj.id}
              className={`objective-item ${isCompleted ? 'completed' : ''} ${isAuto ? 'auto-detected' : ''}`}
              onClick={() => obj.type !== 'per_unit' && !isAuto && toggleObjective(obj.id, objectives[obj.id]?.completed)}
              style={{ cursor: isAdmin && !isAuto && obj.type !== 'per_unit' ? 'pointer' : 'default' }}
            >
              <div className="objective-checkbox">{isCompleted ? '✓' : ''}</div>
              <div className="objective-content">
                <div className="objective-header">
                  <span className="objective-name">{obj.name}</span>
                  <span className="objective-multiplier">{valueLabel}</span>
                </div>
                <p className="objective-description">{obj.description}</p>
                {isAuto && <span className="auto-badge">Auto-detected</span>}

                {/* Youth operators gets a numeric input instead of a toggle */}
                {obj.id === 'youth_operators' && isAdmin && (
                  <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }} onClick={e => e.stopPropagation()}>
                    <label style={{ fontSize: '0.8rem', fontWeight: '600' }}>Youth operators (0–5):</label>
                    <input
                      type="number"
                      min="0"
                      max="5"
                      value={youthCount}
                      onChange={(e) => handleYouthCountChange(e.target.value)}
                      style={{ width: '60px', padding: '0.2rem 0.4rem', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'inherit' }}
                    />
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      = {Math.min(youthCount * obj.value, obj.maxValue)} pts
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!isAdmin && (
        <p className="objectives-note">Only admins can manually mark bonuses as complete.</p>
      )}
    </div>
  );
}

export default ObjectivesTracker;
